import { Link } from 'react-router';
import { Bell, CheckCircle2, ExternalLink, RefreshCcw } from 'lucide-react';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  IconButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
  toast,
} from '@helios/ui';
import { hasPermission, PERMISSIONS } from '@helios/authz';
import { useAuth } from '@/features/auth/authContext';
import { useNotificationRealtime } from '@/hooks/useNotificationRealtime';
import { useNotificationMutations, useSettingsNotifications } from '@/hooks/useSettings';
import type { NotificationItem } from '@/services/settingsClient';
import { useUserPreferences } from '@/features/settings/UserPreferencesProvider';
import { safeNotificationUrl } from '@/features/settings/settingsShared';
import { useSessionScopeFence } from '@/hooks/useSessionScope';

function typeVariant(type: NotificationItem['type']) {
  if (type === 'CRITICAL') return 'critical' as const;
  if (type === 'WARNING') return 'warning' as const;
  if (type === 'SUCCESS') return 'success' as const;
  if (type === 'INFO' || type === 'SYSTEM') return 'info' as const;
  return 'neutral' as const;
}

export function NotificationsMenu() {
  useNotificationRealtime();
  const { permissions } = useAuth();
  const { formatDateTime: formatTime } = useUserPreferences();
  const { identity, isCurrent } = useSessionScopeFence(PERMISSIONS.NOTIFICATIONS_UPDATE);
  const canRead = hasPermission(permissions, PERMISSIONS.NOTIFICATIONS_READ);
  const canUpdate = hasPermission(permissions, PERMISSIONS.NOTIFICATIONS_UPDATE);
  const canOpenNotificationCenter = canRead;
  const notificationsQuery = useSettingsNotifications();
  const mutations = useNotificationMutations();

  const unread = notificationsQuery.summaryError ? undefined : notificationsQuery.summary?.unread;
  const items = notificationsQuery.data?.items ?? [];
  const busy =
    mutations.markRead.isPending ||
    mutations.markAllRead.isPending ||
    Boolean(notificationsQuery.isFetching);

  if (!canRead) {
    return (
      <IconButton
        label="Notifications (no access)"
        disabled
        className="relative rounded-full opacity-50"
      >
        <Bell className="h-4 w-4" aria-hidden="true" />
      </IconButton>
    );
  }

  const handleMarkRead = async (id: string) => {
    const origin = identity;
    try {
      await mutations.markRead.mutateAsync(id);
    } catch (error) {
      if (!isCurrent(origin)) return;
      toast.error(error instanceof Error ? error.message : 'Unable to mark notification read');
    }
  };

  const handleMarkAllRead = async () => {
    const origin = identity;
    try {
      const result = await mutations.markAllRead.mutateAsync();
      if (!isCurrent(origin)) return;
      toast.success(
        result.updated > 0
          ? `Marked ${result.updated} notification(s) read`
          : 'No unread notifications',
      );
    } catch (error) {
      if (!isCurrent(origin)) return;
      toast.error(error instanceof Error ? error.message : 'Unable to mark all read');
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <IconButton label="Notifications" className="relative rounded-full" aria-haspopup="dialog">
          <Bell className="h-4 w-4" aria-hidden="true" />
          {unread !== undefined && unread > 0 ? (
            <span className="bg-gradient-primary text-primary-foreground absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold leading-none shadow-[0_0_10px_hsl(var(--primary)/0.85)]">
              {unread > 99 ? '99+' : unread}
            </span>
          ) : null}
        </IconButton>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[22rem] p-0 sm:w-[24rem]">
        <div className="border-border/60 bg-background/20 flex items-center justify-between gap-2 border-b px-3 py-2.5">
          <div>
            <p className="text-foreground text-sm font-semibold">Notifications</p>
            <p className="text-muted-foreground text-xs">
              {unread === undefined
                ? notificationsQuery.summaryLoading
                  ? 'Checking unread count…'
                  : 'Unread count unavailable'
                : unread > 0
                  ? `${unread} unread`
                  : 'All caught up'}
            </p>
          </div>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              size="xs"
              variant="ghost"
              disabled={busy}
              onClick={() => void notificationsQuery.refetch()}
            >
              <RefreshCcw className="h-3.5 w-3.5" aria-hidden="true" />
              Refresh
            </Button>
            {canUpdate ? (
              <Button
                type="button"
                size="xs"
                variant="ghost"
                disabled={
                  busy ||
                  (unread === undefined ? !items.some((item) => !item.isRead) : unread === 0)
                }
                onClick={() => void handleMarkAllRead()}
              >
                <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                Mark all
              </Button>
            ) : null}
          </div>
        </div>

        <div className="max-h-80 overflow-y-auto p-2">
          {notificationsQuery.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : notificationsQuery.isError ? (
            <ErrorState
              title="Unable to load notifications"
              description={
                notificationsQuery.error instanceof Error
                  ? notificationsQuery.error.message
                  : 'Unknown error'
              }
              action={
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => void notificationsQuery.refetch()}
                >
                  Retry
                </Button>
              }
            />
          ) : items.length === 0 ? (
            <EmptyState
              icon={<Bell className="h-4 w-4" aria-hidden="true" />}
              title="No notifications"
              description="Operator alerts from every module appear here as they are created."
            />
          ) : (
            <ul className="space-y-1.5">
              {items.map((item) => (
                <li
                  key={item.id}
                  className="border-border/70 bg-background/[0.42] shadow-elevation-1 rounded-lg border px-2.5 py-2"
                >
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge variant={typeVariant(item.type)} className="text-[10px]">
                      {item.type}
                    </Badge>
                    <span className="text-muted-foreground text-[11px]">
                      {formatTime(item.createdAt)}
                    </span>
                    {!item.isRead ? (
                      <Badge variant="outline" className="text-[10px]">
                        Unread
                      </Badge>
                    ) : null}
                  </div>
                  <p className="text-foreground mt-1 text-sm font-medium">{item.title}</p>
                  <p className="text-muted-foreground mt-0.5 line-clamp-2 text-xs">{item.body}</p>
                  <div className="mt-1.5 flex flex-wrap items-center gap-2">
                    {safeNotificationUrl(item.actionUrl) ? (
                      <Button size="xs" variant="outline" asChild>
                        <Link to={safeNotificationUrl(item.actionUrl)!}>
                          Open
                          <ExternalLink className="ml-1 h-3 w-3" aria-hidden="true" />
                        </Link>
                      </Button>
                    ) : null}
                    {canUpdate && !item.isRead ? (
                      <Button
                        size="xs"
                        variant="ghost"
                        disabled={busy}
                        onClick={() => void handleMarkRead(item.id)}
                      >
                        Mark read
                      </Button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {canOpenNotificationCenter ? (
          <div className="border-border/60 bg-background/20 border-t px-3 py-2">
            <Button size="xs" variant="ghost" className="w-full justify-center" asChild>
              <Link to="/settings/notifications">Open notification center</Link>
            </Button>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
